import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';
import { toast } from 'sonner';
import {
  Plug, ShieldCheck, FlaskConical, RefreshCw, Save, Check, AlertTriangle, ExternalLink,
} from 'lucide-react';

/**
 * Integrations tab: LevelUp Token Endpoint URL configuration, connection test,
 * and a shortcut link into the SSO Attempts Log tab.
 */
export default function IntegrationsTab({ onViewSsoLog }) {
  const [levelupUrl, setLevelupUrl]           = useState('');
  const [savedLevelupUrl, setSavedLevelupUrl] = useState('');
  const [urlError, setUrlError]               = useState('');
  const [savingUrl, setSavingUrl]             = useState(false);
  const [testingConn, setTestingConn]         = useState(false);
  const [connResult, setConnResult]           = useState(null); // {success, message}

  const loadLevelupUrl = useCallback(async () => {
    try {
      const setting = await api.request('/admin/settings/levelup_api_base_url');
      setLevelupUrl(setting.value || '');
      setSavedLevelupUrl(setting.value || '');
    } catch { /* silently fail — show empty */ }
  }, []);

  useEffect(() => { loadLevelupUrl(); }, [loadLevelupUrl]);

  const validateUrl = (val) => {
    if (!val.startsWith('https://')) return 'URL must start with https://';
    if (!val.includes('{token}')) return 'URL must contain {token} as a placeholder for the LevelUp one-time token';
    if (val.endsWith('/')) return 'Remove the trailing slash after {token}';
    return '';
  };

  const saveLevelupUrl = async () => {
    const err = validateUrl(levelupUrl);
    if (err) { setUrlError(err); return; }
    setUrlError('');
    setSavingUrl(true);
    try {
      await api.request('/admin/settings/levelup_api_base_url', {
        method: 'PUT', body: JSON.stringify({ value: levelupUrl }),
      });
      setSavedLevelupUrl(levelupUrl);
      setConnResult(null);
      toast.success('LevelUp API URL updated. New SSO logins will use this URL.');
    } catch { toast.error('Failed to save URL'); }
    finally { setSavingUrl(false); }
  };

  const testLevelupConn = async () => {
    const err = validateUrl(levelupUrl);
    if (err) { setUrlError(err); return; }
    setUrlError('');
    setTestingConn(true); setConnResult(null);
    try {
      const result = await api.request('/admin/settings/test-levelup', {
        method: 'POST', body: JSON.stringify({ base_url: levelupUrl }),
      });
      setConnResult(result);
    } catch { setConnResult({ success: false, message: 'Test request failed. Check connection.' }); }
    finally { setTestingConn(false); }
  };

  return (
    <div data-testid="integrations-tab" className="space-y-4">
      {/* Section header */}
      <div className="p-4 bg-teal-50 border border-teal-100 rounded-xl">
        <div className="flex items-start gap-2">
          <Plug size={16} className="text-teal-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-teal-800 mb-1">Configure external platform connections</p>
            <p className="text-xs text-teal-700">
              Changes take effect immediately — no server restart required.
            </p>
          </div>
        </div>
      </div>

      {/* LevelUp SSO Card */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        {/* Card header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-gray-50">
          <div className="w-8 h-8 rounded-lg bg-levelup-dark flex items-center justify-center flex-shrink-0">
            <ShieldCheck size={16} className="text-levelup-accent" />
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm">LevelUp Portal SSO</p>
            <p className="text-xs text-gray-500">
              LevelUp redirects users to SIAB via SSO. SIAB validates tokens against the LevelUp API.
            </p>
          </div>
        </div>

        {/* Card body */}
        <div className="px-5 py-5 space-y-4">
          {/* URL input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              LevelUp Token Endpoint URL
            </label>
            <div className="relative">
              <input
                data-testid="levelup-url-input"
                type="url"
                value={levelupUrl}
                onChange={e => { setLevelupUrl(e.target.value); setUrlError(''); setConnResult(null); }}
                placeholder="https://skillupwithlevelup.com/api/v2/fetch-user-details-by-token/{token}"
                className={`w-full border rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 transition-colors ${
                  urlError
                    ? 'border-red-300 bg-red-50 focus:ring-red-300'
                    : 'border-gray-300 focus:ring-levelup-accent'
                }`}
              />
              {levelupUrl !== savedLevelupUrl && !urlError && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                  Unsaved changes
                </span>
              )}
            </div>
            {urlError && (
              <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                <AlertTriangle size={12} /> {urlError}
              </p>
            )}
            <p className="mt-1.5 text-xs text-gray-400">
              Full endpoint URL including <code className="bg-gray-100 px-1 rounded">&#123;token&#125;</code> placeholder.
              Must start with <code className="bg-gray-100 px-1 rounded">https://</code>.
              Changes take effect immediately for all new SSO logins.
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              data-testid="test-levelup-conn-btn"
              onClick={testLevelupConn}
              disabled={testingConn}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-60"
            >
              {testingConn
                ? <><RefreshCw size={14} className="animate-spin" /> Testing...</>
                : <><FlaskConical size={14} /> Test Connection</>
              }
            </button>
            <button
              data-testid="save-levelup-url-btn"
              onClick={saveLevelupUrl}
              disabled={savingUrl || levelupUrl === savedLevelupUrl}
              className="flex items-center gap-2 px-4 py-2 bg-levelup-accent text-levelup-darker rounded-lg text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {savingUrl
                ? <><RefreshCw size={14} className="animate-spin" /> Saving...</>
                : <><Save size={14} /> Save Changes</>
              }
            </button>
          </div>

          {/* Connection test result */}
          {connResult && (
            <div data-testid="conn-result" className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
              connResult.success
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {connResult.success
                ? <Check size={16} className="flex-shrink-0 mt-0.5 text-emerald-600" />
                : <AlertTriangle size={16} className="flex-shrink-0 mt-0.5 text-red-500" />
              }
              <span className="font-medium">{connResult.message}</span>
            </div>
          )}

          {/* Token endpoint info */}
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Resolved endpoint
            </p>
            <p className="text-xs font-mono text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 break-all">
              {levelupUrl
                ? levelupUrl.replace('{token}', '<one-time-token>')
                : <span className="text-gray-400 not-italic">(no URL configured)</span>
              }
            </p>
            <p className="text-xs text-gray-400 mt-1">
              <strong className="text-gray-600">fetch-user-details/</strong> (old) returns <code className="bg-gray-100 px-1 rounded">user_role: "partner"</code> for everyone.&nbsp;
              <strong className="text-gray-600">fetch-user-details-by-token/</strong> (correct) returns <code className="bg-gray-100 px-1 rounded">user_role: "Partner Org RM"</code> properly.
            </p>
          </div>

          {/* Link to SSO Attempts Log */}
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={onViewSsoLog}
              className="flex items-center gap-1.5 text-xs text-levelup-dark hover:underline font-medium"
            >
              <ExternalLink size={12} />
              View SSO Attempts Log to see token details for recent logins
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
